import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCoreContext } from 'spark-core-dx/contexts';
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, TabPaneControl } from 'spark-core-dx/components';
import { useDashboardDomainContext } from '../../DashboardDomainProvider';
import { useSharedData } from '../../hooks/useSharedData';
import { useVisualFormContext } from '../../contexts/VisualFormContext';
import { useGenericManager, useValidationErrors } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import { ItemServiceFactory } from '../../../../services/ItemServiceFactory';
import AIPanel from '../../../../components/AIChat/AIPanel';
import VisualPreviewChart from '../charts/VisualPreviewChart';
import { Design, ExecutionMode, Grouping, ModelBinding, VisualType, Filter, Lock, TextFields, Icon } from "./VisualCreationSteps";
import { normalizeDashboardQueryState } from '../../visualQueryStateUtils';
import {
  buildVisualQueryOutputFields,
  hasExplicitVisualGrouping,
  parseVisualDesignSchema,
  sanitizeVisualConfiguration,
} from '../../visualDesignUtils';
import { buildVisualTypeImageOption } from '../../visualTypeImageCatalog';
import { VISUAL_EXECUTION_MODE } from '../../visualExecutionModes';

const TABLE_KIND = 1;
const VIEW_KIND = 2;
const MODEL_COLUMN_VALUE_KIND_FIELD = 1;
const VISUAL_TAB_MAIN = "visual";
const VISUAL_TAB_GROUPING = "grouping";
const VISUAL_TAB_DESIGN = "design";
const VISUAL_TAB_FILTER = "filter";
const COMPILE_DATA_MODEL_SQL_OPERATION = "CompileDataModelSql";
const COMPILE_QUERY_STATE_CUSTOM_FIELD_KEY = "CompileQueryState";
const FALLBACK_MODEL_KIND_OPTIONS = [
  { Text: 'Table', Value: TABLE_KIND },
  { Text: 'View', Value: VIEW_KIND },
];
const FORM_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '1rem',
  alignItems: 'flex-start',
  width: '100%',
};
const FORM_ROW_PRIMARY_ITEM_STYLE = {
  flex: '1 1 auto',
  minWidth: 0,
};
const FORM_ROW_SECONDARY_ITEM_STYLE = {
  flex: '0 0 25%',
  maxWidth: '25%',
  minWidth: 0,
};

const inferFieldType = (dataTypeName) => {
  const normalized = String(dataTypeName ?? '').toLowerCase();

  if (normalized.includes('bool')) {
    return SystemTypes.UIFieldType.CheckBox;
  }

  if (normalized.includes('date') || normalized.includes('time')) {
    return SystemTypes.UIFieldType.Date;
  }

  return SystemTypes.UIFieldType.TextBox;
};

const mapFilterableColumn = (column, fieldName) => ({
  FieldName: fieldName,
  ColumnName: fieldName,
  SourceFieldName: column?.SourceFieldName || fieldName,
  BindingKind: column?.BindingKind || "column",
  DisplayName: column?.DisplayName || fieldName,
  DataTypeName: column?.DataTypeName || '',
  FieldType: inferFieldType(column?.DataTypeName),
  DataJson: column?.DataJson || '',
  Format: column?.Format,
  CanFilter: column?.CanFilter !== false,
});

const normalizeSelectOptions = (raw) => {
  if (!raw) {
    return [];
  }

  let items = raw?.DataJson ?? raw?.jsonData ?? raw;

  if (Array.isArray(items) && items.length === 1 && Array.isArray(items[0]?.Items)) {
    items = items[0].Items;
  }

  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      items = [];
    }
  }

  if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.Items)) {
    items = items.Items;
  }

  if (Array.isArray(items) && items.length === 1 && Array.isArray(items[0]?.TextValues)) {
    items = items[0].TextValues;
  }

  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const text = item?.Text ?? item?.FriendlyName ?? item?.Name ?? item?.ShortName ?? item?.Label ?? item?.text;
      const value = item?.Value ?? item?._id ?? item?.Id ?? item?.value;

      if (text == null || value == null) {
        return null;
      }

      return {
        Text: String(text),
        Value: value,
      };
    })
    .filter(Boolean);
};

const getSemanticDefinition = (model) => model?.SemanticDefinition ?? {};
const getModelSemanticColumns = (model) =>
  Array.isArray(getSemanticDefinition(model)?.Columns) ? getSemanticDefinition(model).Columns : [];
const getModelSemanticMeasures = (model) =>
  Array.isArray(getSemanticDefinition(model)?.Measures) ? getSemanticDefinition(model).Measures : [];
const getModelKind = (model) => Number(model?.ModelKind ?? 0);

const mapModelSemanticColumn = (column) => {
  const fieldName = String(column?.ValueDefinition?.Value ?? '').trim();
  const bindingName = String(column?.ColumnName ?? '').trim() || fieldName;

  if (
    !bindingName ||
    !fieldName ||
    Number(column?.ValueDefinition?.ValueKind ?? 0) !== MODEL_COLUMN_VALUE_KIND_FIELD ||
    column?.IsDeleted === true ||
    column?.IsActive === false
  ) {
    return null;
  }

  return mapFilterableColumn(
    {
      ...column,
      SourceFieldName: fieldName,
    },
    bindingName
  );
};

const mapModelSemanticPublishedColumn = (column) => {
  const bindingName = String(column?.ColumnName ?? '').trim();

  if (!bindingName || column?.IsDeleted === true || column?.IsActive === false) {
    return null;
  }

  const sourceFieldName = String(column?.ValueDefinition?.Value ?? '').trim();

  return {
    FieldName: bindingName,
    ColumnName: bindingName,
    SourceFieldName: sourceFieldName || bindingName,
    BindingKind: "column",
    DisplayName: column?.DisplayName || bindingName,
    DataTypeName: column?.DataTypeName || '',
    Format: column?.Format,
    SemanticType: column?.SemanticType || '',
    CanFilter: column?.CanFilter !== false,
    CanSort: column?.CanSort !== false,
    CanGroup: column?.CanGroup !== false,
    IsVisible: column?.IsVisible !== false,
    Aggregation: "none",
  };
};

const mapModelSemanticMeasure = (measure) => {
  const fieldName = String(measure?.MeasureName ?? '').trim();

  if (!fieldName || measure?.IsDeleted === true || measure?.IsActive === false) {
    return null;
  }

  return {
    FieldName: fieldName,
    ColumnName: fieldName,
    SourceFieldName: String(measure?.MeasureDefinition?.TargetName ?? '').trim() || fieldName,
    BindingKind: "measure",
    DisplayName: measure?.DisplayName || fieldName,
    DataTypeName: measure?.DataTypeName || '',
    Format: measure?.Format,
    SemanticType: "measure",
    CanFilter: false,
    CanSort: true,
    CanGroup: false,
    IsVisible: measure?.IsVisible !== false,
    Aggregation: String(measure?.AggregationKind ?? '').trim(),
  };
};

const mapModelOptions = (items = []) =>
  items.map((item) => ({
    Kind: getModelKind(item),
    Value: item?._id,
    Text: item?.Label || item?.Key || item?.Name || item?._id,
    item,
    columns: getModelSemanticColumns(item)
      .map((column) => mapModelSemanticColumn(column))
      .filter((column) => !!column?.FieldName),
    semanticColumns: getModelSemanticColumns(item)
      .map((column) => mapModelSemanticPublishedColumn(column))
      .filter((column) => !!column?.FieldName),
    measures: getModelSemanticMeasures(item)
      .map((measure) => mapModelSemanticMeasure(measure))
      .filter((measure) => !!measure?.FieldName),
  }));

const mapVisualTypeOptions = (items = []) =>
  items
    .map((item) => {
      const text = item?.FriendlyName || item?.Name || item?.ShortName || item?._id;
      const value = item?._id ?? item?.Value;

      if (!text || !value) {
        return null;
      }

      return {
        Text: text,
        Value: value,
        Disabled: item?.IsActive !== true || !item?.ManualBindingSchemaJson,
        ...buildVisualTypeImageOption(item),
      };
    })
    .filter(Boolean);

const unwrapServiceResult = (response) => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response ?? null;
};

const getValidationDisplayMessages = (result) =>
  Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
    .map((entry) => String(entry?.DisplayMessage ?? entry?.Content ?? "").trim())
    .filter(Boolean);

const VisualCreationDialog = () => {
  const coreContext = useCoreContext();
  const { dashboardLib, activeDashboard } = useDashboardDomainContext();
  const { visualTypes } = useSharedData();
  const { visualObject, setVisualObject, closeVisualCreationDialog } = useVisualFormContext();
  const { hasAndShowValidationErrors } = useValidationErrors();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const visualMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Visual, false);
  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel, false);
  const visualTypeMgr = useGenericManager("VisualType", false);
  const visualMgrRef = useRef(visualMgr);
  const modelMgrRef = useRef(modelMgr);
  const visualTypeMgrRef = useRef(visualTypeMgr);
  const [modelOptions, setModelOptions] = useState([]);
  const [availableVisualTypes, setAvailableVisualTypes] = useState([]);
  const [availableVisualTypeItems, setAvailableVisualTypeItems] = useState([]);
  const [modelKindOptions, setModelKindOptions] = useState(FALLBACK_MODEL_KIND_OPTIONS);
  const [activeTab, setActiveTab] = useState(VISUAL_TAB_MAIN);
  const [saveError, setSaveError] = useState("");
  const [isLoadingModelOptions, setIsLoadingModelOptions] = useState(false);
  const [isVisualSqlDialogOpen, setIsVisualSqlDialogOpen] = useState(false);
  const [isVisualSqlLoading, setIsVisualSqlLoading] = useState(false);
  const [visualSqlText, setVisualSqlText] = useState("");
  const [visualSqlError, setVisualSqlError] = useState("");
  const modelLoadKeyRef = useRef(null);
  const visualTypeLoadKeyRef = useRef(null);


  const isCreate = !visualObject?.VisualId;
  const isDialogOpen = !!visualObject;
  const isSystemAdmin = useMemo(
    () => Array.isArray(coreContext?.ProfileRoles) && coreContext.ProfileRoles.some((role) => role?.IsSystemAdmin === true),
    [coreContext?.ProfileRoles]
  );
  const selectedModel = useMemo(
    () => modelOptions.find((option) => String(option?.Value) === String(visualObject?.ModelId)) ?? null,
    [modelOptions, visualObject?.ModelId]
  );
  const selectedModelColumns = useMemo(
    () => selectedModel?.columns ?? [],
    [selectedModel]
  );
  const selectedModelSemanticColumns = useMemo(
    () => selectedModel?.semanticColumns ?? [],
    [selectedModel]
  );
  const selectedModelMeasures = useMemo(
    () => selectedModel?.measures ?? [],
    [selectedModel]
  );
  const selectedQueryOutputFields = useMemo(
    () => buildVisualQueryOutputFields(visualObject?.DefaultQueryState, selectedModelSemanticColumns, selectedModelMeasures),
    [selectedModelMeasures, selectedModelSemanticColumns, visualObject?.DefaultQueryState]
  );
  const selectedFieldCatalog = useMemo(() => {
    const seen = new Set();
    const nextFields = [];

    [...selectedModelSemanticColumns, ...selectedModelMeasures, ...selectedQueryOutputFields].forEach((field) => {
      const fieldName = String(field?.FieldName ?? '').trim();
      if (!fieldName) {
        return;
      }

      const dedupeKey = fieldName.toLowerCase();
      if (seen.has(dedupeKey)) {
        return;
      }

      seen.add(dedupeKey);
      nextFields.push(field);
    });

    return nextFields;
  }, [selectedModelMeasures, selectedModelSemanticColumns, selectedQueryOutputFields]);
  const selectedBindingFields = useMemo(
    () => (hasExplicitVisualGrouping(visualObject?.DefaultQueryState) ? selectedQueryOutputFields : selectedModelColumns),
    [selectedModelColumns, selectedQueryOutputFields, visualObject?.DefaultQueryState]
  );
  const selectedVisualType = useMemo(
    () => availableVisualTypeItems.find((item) => String(item?._id ?? item?.Value) === String(visualObject?.VisualTypeId)) ?? null,
    [availableVisualTypeItems, visualObject?.VisualTypeId]
  );
  const selectedVisualSchema = useMemo(
    () => parseVisualDesignSchema(selectedVisualType, visualObject),
    [selectedVisualType, visualObject]
  );
  const previewVisual = useMemo(
    () => sanitizeVisualConfiguration(visualObject, selectedVisualSchema, selectedFieldCatalog, selectedBindingFields),
    [selectedBindingFields, selectedFieldCatalog, selectedVisualSchema, visualObject]
  );
  const selectedExecutionMode = Number(previewVisual?.ExecutionMode ?? visualObject?.ExecutionMode ?? VISUAL_EXECUTION_MODE.REALTIME);
  const isRealtimeExecutionMode = selectedExecutionMode === VISUAL_EXECUTION_MODE.REALTIME;
  const dashboardWorkspaceId = String(activeDashboard?.DataModelWorkspaceId ?? "").trim();
  const isWorkspaceRepairRequired = isDialogOpen && !!activeDashboard?._id && !dashboardWorkspaceId;

  const handleSubmit = async () => {
    setSaveError("");
    if (hasAndShowValidationErrors("visual")) {
      return;
    }

    const result = await handleSave();
    const normalizedResult = Array.isArray(result) ? result?.[0] : result;

    if (normalizedResult?.Success) {
      closeVisualCreationDialog();
      return;
    }

    const validationDisplayMessage = Object.values(normalizedResult?.ValidationErrors ?? {})
      .map((entry) => entry?.DisplayMessage ?? entry?.Content ?? "")
      .find(Boolean);

    setSaveError(
      validationDisplayMessage ??
      normalizedResult?.DisplayMessage ??
      normalizedResult?.Message?.DisplayMessage ??
      normalizedResult?.Message?.Content ??
      "Unable to save visual."
    );
  };

  const handleSave = async () => {
    const saveFn = isCreate ? dashboardLib.createVisual : dashboardLib.editVisual;
    const nextVisualObject = sanitizeVisualConfiguration(visualObject, selectedVisualSchema, selectedFieldCatalog, selectedBindingFields);

    return await saveFn({
      ...nextVisualObject,
      DefaultQueryState: normalizeDashboardQueryState(nextVisualObject?.DefaultQueryState, {
        ModelKind: Number(nextVisualObject?.ModelKind ?? 0),
        ModelId: nextVisualObject?.ModelId ?? '',
      }),
    });
  };

  useEffect(() => {
    visualMgrRef.current = visualMgr;
  }, [visualMgr]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  useEffect(() => {
    visualTypeMgrRef.current = visualTypeMgr;
  }, [visualTypeMgr]);

  useEffect(() => {
      if (!isDialogOpen) {
        modelLoadKeyRef.current = null;
        visualTypeLoadKeyRef.current = null;
        setIsLoadingModelOptions(false);
        setModelKindOptions(FALLBACK_MODEL_KIND_OPTIONS);
        setAvailableVisualTypeItems([]);
        setActiveTab(VISUAL_TAB_MAIN);
        setSaveError("");
        setIsVisualSqlDialogOpen(false);
        setIsVisualSqlLoading(false);
        setVisualSqlText("");
        setVisualSqlError("");
        return;
      }
  }, [isDialogOpen]);

  useEffect(() => {
    let alive = true;

    const loadModelOptions = async () => {
      if (!isDialogOpen) {
        return;
      }

      if (!activeDashboard?._id) {
        if (alive) {
          setIsLoadingModelOptions(false);
          setModelOptions([]);
        }
        return;
      }

      const requestKey = `${activeDashboard._id}:${dashboardWorkspaceId}`;
      if (modelLoadKeyRef.current === requestKey) {
        return;
      }
      modelLoadKeyRef.current = requestKey;

      if (!dashboardWorkspaceId) {
        if (alive) {
          setIsLoadingModelOptions(false);
          setModelOptions([]);
        }
        return;
      }

      setIsLoadingModelOptions(true);

      try {
        const result = await modelMgrRef.current?.Query({
          model: "x => x.IsActive == true && x.IsDeleted == false",
        });

        if (!alive) {
          return;
        }

        const workspaceId = dashboardWorkspaceId;
        const currentModelId = String(visualObject?.ModelId ?? "").trim();
        let scopedModels = (Array.isArray(result?.Items) ? result.Items : []).filter((item) =>
          String(item?.DataModelWorkspaceId ?? "").trim() === workspaceId
        );

        const hasCurrentModel = currentModelId
          ? scopedModels.some((item) => String(item?._id ?? item?.UniqueId ?? "").trim() === currentModelId)
          : true;

        if (currentModelId && !hasCurrentModel) {
          const currentModelResult = await modelMgrRef.current?.getItem?.(currentModelId);
          const currentModelItem = Array.isArray(currentModelResult?.Items)
            ? currentModelResult.Items[0]
            : null;

          if (
            currentModelItem &&
            String(currentModelItem?.DataModelWorkspaceId ?? "").trim() === workspaceId
          ) {
            scopedModels = [...scopedModels, currentModelItem];
          }
        }

        const nextModelOptions = mapModelOptions(scopedModels)
          .filter((option) => Number(option?.Kind ?? 0) === TABLE_KIND || Number(option?.Kind ?? 0) === VIEW_KIND)
          .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));

        setModelOptions(nextModelOptions);
      } finally {
        if (alive) {
          setIsLoadingModelOptions(false);
        }
      }
    };

    void loadModelOptions();

    return () => {
      alive = false;
    };
  }, [activeDashboard?._id, dashboardWorkspaceId, isDialogOpen, visualObject?.ModelId]);

  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }

    const currentModelId = String(visualObject?.ModelId ?? "").trim();
    if (!currentModelId) {
      return;
    }

    if (isLoadingModelOptions || isWorkspaceRepairRequired || modelOptions.length === 0) {
      return;
    }

    const isModelInScope = modelOptions.some((option) => String(option?.Value ?? "").trim() === currentModelId);
    if (isModelInScope) {
      return;
    }

    setVisualObject((prev) => {
      if (String(prev?.ModelId ?? "").trim() !== currentModelId) {
        return prev;
      }

      return {
        ...prev,
        ModelId: "",
        DefaultQueryState: normalizeDashboardQueryState(prev?.DefaultQueryState, {
          ModelKind: Number(prev?.ModelKind ?? 0),
          ModelId: "",
        }),
      };
    });
  }, [isDialogOpen, isLoadingModelOptions, isWorkspaceRepairRequired, modelOptions, setVisualObject, visualObject?.ModelId]);

  useEffect(() => {
    let alive = true;

    const loadVisualTypes = async () => {
      if (!isDialogOpen) {
        return;
      }

      if (visualTypeLoadKeyRef.current === "loaded") {
        return;
      }
      visualTypeLoadKeyRef.current = "loaded";

      const metadata = await visualMgrRef.current?.GetMetadata?.({});
      const visualFields = metadata?.Items?.[0]?.Fields ?? [];
      const modelKindField = visualFields.find((field) => field?.FieldName === "ModelKind");

      if (alive) {
        const nextModelKinds = normalizeSelectOptions(modelKindField?.DataJson);
        setModelKindOptions(nextModelKinds.length > 0 ? nextModelKinds : FALLBACK_MODEL_KIND_OPTIONS);
      }

      if ((visualTypes?.length ?? 0) > 0) {
        if (alive) {
          setAvailableVisualTypeItems(visualTypes);
          setAvailableVisualTypes(mapVisualTypeOptions(visualTypes));
        }
      }

      const result = await visualTypeMgrRef.current?.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });

      if (alive) {
        const nextItems = result?.Items ?? [];
        if (nextItems.length > 0) {
          setAvailableVisualTypeItems(nextItems);
          setAvailableVisualTypes(mapVisualTypeOptions(nextItems));
        }
      }
    };

    void loadVisualTypes();

    return () => {
      alive = false;
    };
  }, [isDialogOpen, visualTypes?.length]);

  useEffect(() => {
    if (!visualObject) {
      return;
    }

    const currentIcon = visualObject?.Icon;
    const legacyIcon = visualObject?.icon;
    const shouldHydrateDefaultQueryState = !visualObject?.DefaultQueryState;
    const shouldPromoteLegacyIcon = legacyIcon !== undefined || currentIcon == null;

    if (!shouldHydrateDefaultQueryState && !shouldPromoteLegacyIcon) {
      return;
    }

    setVisualObject((prev) => {
      const {
        VisualColorPaletteOptionId,
        VisualRenderOptionId,
        PaletteName,
        ...rest
      } = prev ?? {};

      return {
        ...rest,
        Icon: rest?.Icon ?? prev?.icon ?? "",
        DefaultQueryState: normalizeDashboardQueryState(rest?.DefaultQueryState, {
          ModelKind: Number(rest?.ModelKind ?? 0),
          ModelId: rest?.ModelId ?? '',
        }),
      };
    });
  }, [
    setVisualObject,
    visualObject?.DefaultQueryState,
    visualObject?.Icon,
    visualObject?.icon,
    visualObject?.ModelKind,
    visualObject?.ModelId,
  ]);

  useEffect(() => {
    if (activeTab === VISUAL_TAB_DESIGN && (!selectedModel || !selectedVisualType)) {
      setActiveTab(VISUAL_TAB_MAIN);
      return;
    }

    if ((activeTab === VISUAL_TAB_GROUPING || activeTab === VISUAL_TAB_FILTER) && !selectedModel) {
      setActiveTab(VISUAL_TAB_MAIN);
    }
  }, [activeTab, selectedModel, selectedVisualType]);

  const closeVisualSqlDialog = () => {
    setIsVisualSqlDialogOpen(false);
    setVisualSqlError("");
  };

  const handleOpenVisualSqlDialog = async () => {
    const modelItem = selectedModel?.item ?? null;
    if (!modelItem || isRealtimeExecutionMode) {
      return;
    }

    setIsVisualSqlDialogOpen(true);
    setIsVisualSqlLoading(true);
    setVisualSqlText("");
    setVisualSqlError("");

    try {
      const nextVisualObject = sanitizeVisualConfiguration(
        visualObject,
        selectedVisualSchema,
        selectedFieldCatalog,
        selectedBindingFields
      );
      const nextQueryState = normalizeDashboardQueryState(nextVisualObject?.DefaultQueryState, {
        ModelKind: Number(nextVisualObject?.ModelKind ?? 0),
        ModelId: nextVisualObject?.ModelId ?? "",
      });
      const nextModelItem = {
        ...modelItem,
        CustomFields: {
          ...(modelItem?.CustomFields ?? {}),
          [COMPILE_QUERY_STATE_CUSTOM_FIELD_KEY]: JSON.stringify(nextQueryState),
        },
      };

      const rawResponse = await modelMgrRef.current?.RunOperation({
        operationName: COMPILE_DATA_MODEL_SQL_OPERATION,
        id: modelItem?._id,
        item: nextModelItem,
        queryParams: {
          executionMode: selectedExecutionMode,
        },
        isItem: true,
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        setVisualSqlError(
          validationMessages.length > 0
            ? validationMessages.join(" ")
            : response?.DisplayMessage || response?.Message?.DisplayMessage || "Unable to compile visual query SQL."
        );
        return;
      }

      const nextSql = typeof response?.Value === "string"
        ? response.Value
        : String(response?.Value?.Sql ?? response?.Value ?? "").trim();

      setVisualSqlText(nextSql);
      if (!nextSql) {
        setVisualSqlError("No SQL was returned for this visual query.");
      }
    } catch (error) {
      console.error("Data model visual SQL compile failed:", error);
      setVisualSqlError("Unable to compile visual query SQL.");
    } finally {
      setIsVisualSqlLoading(false);
    }
  };

  const visualTabContent = (
    <div className="dialog-text visual-dialog-text visual-dialog-text--main">
      {saveError ? (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.75rem",
            border: "1px solid var(--error)",
            background: "rgba(201, 43, 43, 0.08)",
            color: "var(--error)",
            fontWeight: 600,
          }}
        >
          {saveError}
        </div>
      ) : null}
      {isWorkspaceRepairRequired ? (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.75rem",
            border: "1px solid var(--error)",
            background: "rgba(201, 43, 43, 0.08)",
            color: "var(--error)",
            fontWeight: 600,
          }}
        >
          This dashboard is not linked to a valid model workspace. Repair the dashboard&apos;s model workspace before binding visuals to a model.
        </div>
      ) : null}
      <div style={FORM_ROW_STYLE}>
        <div className="visual-attribute" style={FORM_ROW_SECONDARY_ITEM_STYLE}>
          <Icon />
        </div>
        <div className="visual-attribute" style={FORM_ROW_PRIMARY_ITEM_STYLE}>
          <TextFields fieldName={"Name"} displayName={"Visual Name"} />
        </div>
      </div>
      <div className="visual-attribute">
        <TextFields fieldName={"Description"} displayName={"Description"} />
      </div>
      <div className="visual-attribute">
        <ModelBinding
          inline={true}
          modelKindOptions={modelKindOptions}
          modelOptions={modelOptions}
          disableModelSelection={isWorkspaceRepairRequired}
          unavailableMessage={isWorkspaceRepairRequired ? "Dashboard model workspace is missing" : "No models available"}
        />
      </div>
      <div className="visual-attribute visual-attribute--fill">
        <VisualType options={availableVisualTypes} />
      </div>
    </div>
  );

  const filterTabContent = (
    <div className="dialog-text">
      {saveError ? (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.75rem",
            border: "1px solid var(--error)",
            background: "rgba(201, 43, 43, 0.08)",
            color: "var(--error)",
            fontWeight: 600,
          }}
        >
          {saveError}
        </div>
      ) : null}
      <div className="visual-attribute">
        <Lock />
      </div>
      <div className="visual-attribute">
        <Filter selectedColumns={selectedModelColumns} />
      </div>
    </div>
  );

  const groupingTabContent = (
    <div className="dialog-text">
      {saveError ? (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.75rem",
            border: "1px solid var(--error)",
            background: "rgba(201, 43, 43, 0.08)",
            color: "var(--error)",
            fontWeight: 600,
          }}
        >
          {saveError}
        </div>
      ) : null}
      <div className="visual-attribute">
        <Grouping
          selectedModelColumns={selectedModelSemanticColumns}
          selectedModelMeasures={selectedModelMeasures}
        />
      </div>
    </div>
  );

  const designTabContent = (
    <div className="dialog-text">
      {saveError ? (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.75rem",
            border: "1px solid var(--error)",
            background: "rgba(201, 43, 43, 0.08)",
            color: "var(--error)",
            fontWeight: 600,
          }}
        >
          {saveError}
        </div>
      ) : null}
      <div className="visual-attribute">
        <ExecutionMode />
      </div>
      <div className="visual-attribute">
        <Design
          visualType={selectedVisualType}
          selectedBindingColumns={selectedBindingFields}
          selectedFieldCatalog={selectedFieldCatalog}
        />
      </div>
    </div>
  );

  const tabs = [
    { key: VISUAL_TAB_MAIN, title: "Visual", content: visualTabContent },
    {
      key: VISUAL_TAB_GROUPING,
      title: "Grouping",
      content: groupingTabContent,
      disabled: !selectedModel,
    },
    { key: VISUAL_TAB_FILTER, title: "Filter", content: filterTabContent, disabled: !selectedModel },
    {
      key: VISUAL_TAB_DESIGN,
      title: "Design",
      content: designTabContent,
      disabled: !selectedModel || !selectedVisualType,
    },
  ];
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeTab));

  return (
    <>
    <AIPanel
      open={isDialogOpen}
      title={isCreate ? "Design a New Visual" : "Edit Visual"}
      onCancel={closeVisualCreationDialog}
      mapToClass="core-layout"
      chatPane="right"
      chatPaneCollapseMode="open"
      enableContentDragHiding={false}
      minLeft={480}
      minMiddle={520}
      minRight={360}
      referenceName="Visual"
      referenceId={visualObject?.VisualId ?? visualObject?._id}
      introMessage="Lets configure your visual!"
      splashMessage="Coming soon..."
      middle={(
        <VisualPreviewChart
          visual={previewVisual}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      )}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          flex: "1 1 auto",
          height: "100%",
          width: "100%",
          minWidth: 0,
          background: "var(--primary-background)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position:"absolute",
            justifyContent: "flex-start",
            minHeight: "2.25rem",
          }}
        >
          {isSystemAdmin ? (
            <span
              title={
                isRealtimeExecutionMode
                  ? "SQL compilation is available for Client and Server execution modes."
                  : "Compile query SQL"
              }
            >
              <ButtonControl
                type="icon"
                onClick={handleOpenVisualSqlDialog}
                disabled={!selectedModel || isRealtimeExecutionMode || isVisualSqlLoading}
                title={
                  isRealtimeExecutionMode
                    ? "SQL compilation is available for Client and Server execution modes."
                    : "Compile query SQL"
                }
              >
                <IconControl
                  iconKey="bug"
                  title={
                    isRealtimeExecutionMode
                      ? "SQL compilation is available for Client and Server execution modes."
                      : "Compile query SQL"
                  }
                />
              </ButtonControl>
            </span>
          ) : null}
        </div>
        <div
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            padding: 0,
          }}
        >
          {visualObject ? (
            <TabPaneControl
              tabs={tabs}
              activeIndex={activeIndex}
              onChange={(nextIndex) => setActiveTab(tabs[nextIndex]?.key ?? VISUAL_TAB_MAIN)}
              align="right"
              compact={true}
              framed={false}
              unmountInactive={true}
            />
          ) : null}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.75rem",
            marginTop: "auto",
            padding: "1rem",
            borderTop: "1px solid var(--primary-border)",
            background: "var(--secondary-background)",
          }}
        >
          <ButtonControl type="cancel" onClick={closeVisualCreationDialog}>
            Cancel
          </ButtonControl>
          <ButtonControl type="ok" onClick={handleSubmit}>
            Save Visual
          </ButtonControl>
        </div>
      </div>
    </AIPanel>
    <DialogControl
      openDialog={isVisualSqlDialogOpen}
      title="Visual Query SQL"
      cancelText="Close"
      onCancel={closeVisualSqlDialog}
      canFullScreen={true}
    >
      <div style={{ minHeight: "18rem" }}>
        {isVisualSqlLoading ? (
          <div
            style={{
              minHeight: "18rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            <div>Compiling SQL</div>
            <Loading type="spinner-2" size="1.5rem" />
          </div>
        ) : visualSqlError ? (
          <div
            style={{
              minHeight: "18rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "0.75rem",
              color: "var(--error)",
              textAlign: "center",
            }}
          >
            <div>SQL unavailable</div>
            <div>{visualSqlError}</div>
          </div>
        ) : (
          <FieldControl
            fieldMetadata={{
              DisplayName: "Compiled Query SQL",
              Description: "Compiled SQL for the selected visual query state and execution mode.",
              FieldName: "compiledVisualSql",
              FieldType: SystemTypes.UIFieldType.SQL,
              IsRequired: false,
            }}
            value={visualSqlText}
            disabled={true}
            onChange={() => {}}
          />
        )}
      </div>
    </DialogControl>
    </>
  );
};

export default VisualCreationDialog;
